/**
 * Validated Job Status Form
 * 
 * Production-ready form for creating and managing background jobs with comprehensive validation
 */

import {
    ActionIcon,
    Alert,
    Badge,
    Button,
    Card,
    Code,
    Group,
    JsonInput,
    NumberInput,
    Paper,
    Progress,
    Select,
    Stack,
    Text,
    Textarea,
    TextInput,
    Timeline,
    Title,
    Tooltip
} from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import {
    IconAlertTriangle,
    IconCheck,
    IconInfoCircle,
    IconPlayerPause,
    IconPlayerPlay,
    IconRefresh,
    IconTrash,
    IconX
} from '@tabler/icons-react';
import React, { useEffect, useState } from 'react';
import { useJob, useJobLogs, useJobMutations } from '../data/hooks/useJobStatus';
import { useValidatedForm, useValidatedFormSubmission } from '../hooks/useValidatedForm';

interface JobData {
    jobType: string;
    name: string;
    description?: string;
    priority: 'low' | 'normal' | 'high' | 'urgent';
    maxRetries?: number;
    retryBackoff?: number;
    parameters?: Record<string, any>;
    scheduledFor?: string;
    timeout?: number;
    tags?: string[];
}

interface ValidatedJobStatusFormProps {
    initialData?: Partial<JobData>;
    jobId?: string;
    mode: 'create' | 'edit' | 'view';
    onSuccess?: (data: any) => void;
    onCancel?: () => void;
}

export const ValidatedJobStatusForm: React.FC<ValidatedJobStatusFormProps> = ({
    initialData,
    jobId,
    mode,
    onSuccess,
    onCancel
}) => {
    const { createJob, updateProgress, cancelJob, retryJob, deleteJob } = useJobMutations();
    const { job, loading: jobLoading, isActive, refetch } = useJob(jobId || null, {
        autoRefresh: mode === 'view' && !!jobId
    });
    const { logs, loading: logsLoading, refetch: refetchLogs } = useJobLogs(jobId ?? null, {
        autoRefresh: mode === 'view' && !!jobId
    });
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [showLogs, setShowLogs] = useState(false);

    // Initialize validated form
    const form = useValidatedForm<Partial<JobData>>({
        schemaName: 'JobStatus' as any,
        initialValues: {
            jobType: initialData?.jobType || job?.jobType || '',
            name: initialData?.name || job?.name || '',
            description: initialData?.description || job?.description || '',
            priority: initialData?.priority || job?.priority || 'normal',
            maxRetries: initialData?.maxRetries || job?.maxRetries || 3,
            retryBackoff: initialData?.retryBackoff || job?.retryBackoff || 5000,
            parameters: initialData?.parameters || job?.parameters || {},
            scheduledFor: initialData?.scheduledFor || undefined,
            timeout: initialData?.timeout || 300000,
            tags: initialData?.tags || []
        },
        validateOnChange: true,
        validateOnBlur: true,
        showNotifications: false
    });

    // Form submission with GraphQL
    const { submitForm, loading: submitting, submissionResult } = useValidatedFormSubmission(
        'JobStatus' as any,
        () => ({
            mutate: async (data: Partial<JobData>) => {
                if (mode === 'create') {
                    return await createJob(data as any);
                } else {
                    // For edit mode, we could add job update functionality
                    throw new Error('Job editing not yet implemented');
                }
            },
            loading: false,
            error: null
        })
    );

    // Job type options
    const jobTypeOptions = [
        { value: 'data_processing', label: 'Data Processing - ETL operations and transformations' },
        { value: 'report_generation', label: 'Report Generation - Create scheduled reports' },
        { value: 'email_notification', label: 'Email Notification - Send notifications and alerts' },
        { value: 'file_processing', label: 'File Processing - Process uploaded files' },
        { value: 'api_sync', label: 'API Sync - Synchronize with external APIs' },
        { value: 'cleanup', label: 'Cleanup - Maintenance and cleanup tasks' },
        { value: 'backup', label: 'Backup - Data backup operations' },
        { value: 'migration', label: 'Migration - Data migration tasks' },
        { value: 'validation', label: 'Validation - Validate data integrity' },
        { value: 'custom', label: 'Custom - Custom job implementation' }
    ];

    // Priority options
    const priorityOptions = [
        { value: 'low', label: 'Low Priority - Background processing' },
        { value: 'normal', label: 'Normal Priority - Standard processing' },
        { value: 'high', label: 'High Priority - Expedited processing' },
        { value: 'urgent', label: 'Urgent Priority - Immediate processing' }
    ];

    // Timeout presets (in milliseconds)
    const timeoutPresets = [
        { value: 30000, label: '30 seconds' },
        { value: 60000, label: '1 minute' },
        { value: 300000, label: '5 minutes' },
        { value: 900000, label: '15 minutes' },
        { value: 1800000, label: '30 minutes' },
        { value: 3600000, label: '1 hour' },
        { value: 7200000, label: '2 hours' },
        { value: 14400000, label: '4 hours' }
    ];

    // Update form when job data loads
    useEffect(() => {
        if (job && mode === 'view') {
            form.setValues({
                jobType: job.jobType,
                name: job.name,
                description: job.description,
                priority: job.priority as any,
                maxRetries: job.maxRetries,
                retryBackoff: job.retryBackoff,
                parameters: job.parameters
            });
        }
    }, [job, mode]);

    // Handle form submission
    const handleSubmit = async (values: Partial<JobData>) => {
        const result = await submitForm(values);
        if (result.success && onSuccess) {
            onSuccess(result.data);
        }
    };

    // Handle job actions
    const handleJobAction = async (action: 'cancel' | 'retry' | 'delete') => {
        if (!jobId) return;

        try {
            let result = null;
            switch (action) {
                case 'cancel':
                    result = await cancelJob(jobId, 'Cancelled by user');
                    break;
                case 'retry':
                    result = await retryJob(jobId);
                    break;
                case 'delete':
                    const deleted = await deleteJob(jobId);
                    if (deleted) {
                        notifications.show({
                            title: 'Success',
                            message: 'Job deleted successfully',
                            color: 'green'
                        });
                        onSuccess?.(null);
                        return;
                    }
                    break;
            }

            if (result) {
                notifications.show({
                    title: 'Success',
                    message: `Job ${action}led successfully`,
                    color: 'green'
                });
                refetch();
            }
        } catch (error) {
            notifications.show({
                title: 'Error',
                message: `Failed to ${action} job: ${(error as Error).message}`,
                color: 'red'
            });
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'queued': return 'blue';
            case 'running': return 'orange';
            case 'completed': return 'green';
            case 'failed': return 'red';
            case 'cancelled': return 'gray';
            case 'retrying': return 'yellow';
            default: return 'gray';
        }
    };

    const formatDuration = (ms: number) => {
        if (ms < 1000) return `${ms}ms`;
        if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
        if (ms < 3600000) return `${(ms / 60000).toFixed(1)}m`;
        return `${(ms / 3600000).toFixed(1)}h`;
    };

    return (
        <form onSubmit={mode !== 'view' ? form.onSubmit((values) => handleSubmit(values as Partial<JobData>)) : undefined}>
            <Stack gap="lg">
                {/* Header */}
                <Paper p="md" withBorder>
                    <Group justify="space-between">
                        <div>
                            <Group>
                                <Title order={3}>
                                    {mode === 'create' ? 'Create' : mode === 'edit' ? 'Edit' : 'View'} Job
                                </Title>
                                {job && (
                                    <Badge
                                        size="lg"
                                        color={getStatusColor(job.status)}
                                        variant="filled"
                                    >
                                        {job.status.toUpperCase()}
                                    </Badge>
                                )}
                            </Group>
                            <Text size="sm" c="dimmed">
                                {mode === 'view' ?
                                    `Job ID: ${jobId}` :
                                    'Job data is validated using JSON Schema'
                                }
                            </Text>
                        </div>

                        <Group>
                            {mode !== 'view' && (
                                <Badge
                                    color={form.validationResult.valid ? 'green' : 'red'}
                                    leftSection={form.validationResult.valid ? <IconCheck size={14} /> : <IconX size={14} />}
                                >
                                    {form.validationResult.valid ? 'Valid' : `${form.validationResult.errors.length} Error${form.validationResult.errors.length > 1 ? 's' : ''}`}
                                </Badge>
                            )}

                            {mode === 'view' && job && (
                                <Group>
                                    <Tooltip label="Refresh">
                                        <ActionIcon onClick={() => refetch()} variant="default">
                                            <IconRefresh size={18} />
                                        </ActionIcon>
                                    </Tooltip>

                                    {isActive && (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            color="red"
                                            leftSection={<IconPlayerPause size={16} />}
                                            onClick={() => handleJobAction('cancel')}
                                        >
                                            Cancel
                                        </Button>
                                    )}

                                    {job.status === 'failed' && (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            color="blue"
                                            leftSection={<IconPlayerPlay size={16} />}
                                            onClick={() => handleJobAction('retry')}
                                        >
                                            Retry
                                        </Button>
                                    )}

                                    {['completed', 'failed', 'cancelled'].includes(job.status) && (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            color="red"
                                            leftSection={<IconTrash size={16} />}
                                            onClick={() => handleJobAction('delete')}
                                        >
                                            Delete
                                        </Button>
                                    )}
                                </Group>
                            )}
                        </Group>
                    </Group>
                </Paper>

                {/* Job Status Details (View Mode) */}
                {mode === 'view' && job && (
                    <Card withBorder>
                        <Title order={4} mb="md">Job Status</Title>
                        <Stack gap="sm">
                            <Group>
                                <Text fw={500} w={120}>Progress:</Text>
                                <Progress
                                    value={job.progress || 0}
                                    style={{ flex: 1 }}
                                    color={getStatusColor(job.status)}
                                />
                                <Text>{job.progress || 0}%</Text>
                            </Group>

                            {job.lastError && (
                                <Group>
                                    <Text fw={500} w={120}>Message:</Text>
                                    <Text>{job.lastError}</Text>
                                </Group>
                            )}

                            <Group>
                                <Text fw={500} w={120}>Created:</Text>
                                <Text>{new Date(job.queuedAt).toLocaleString()}</Text>
                            </Group>

                            {job.startedAt && (
                                <Group>
                                    <Text fw={500} w={120}>Started:</Text>
                                    <Text>{new Date(job.startedAt).toLocaleString()}</Text>
                                </Group>
                            )}

                            {job.completedAt && (
                                <Group>
                                    <Text fw={500} w={120}>Finished:</Text>
                                    <Text>{new Date(job.completedAt).toLocaleString()}</Text>
                                </Group>
                            )}

                            {job.duration && (
                                <Group>
                                    <Text fw={500} w={120}>Duration:</Text>
                                    <Text>{formatDuration(job.duration)}</Text>
                                </Group>
                            )}

                            {job.lastError && (
                                <Alert color="red" title="Error" mt="md">
                                    <Code>{job.lastError}</Code>
                                </Alert>
                            )}
                        </Stack>
                    </Card>
                )}

                {/* Validation Summary */}
                {mode !== 'view' && form.validationResult.errors.length > 0 && (
                    <Alert color="red" title="Validation Errors" icon={<IconAlertTriangle />}>
                        <Stack gap="xs">
                            {form.validationResult.errors.map((error, index) => (
                                <Text key={index} size="sm">
                                    <strong>{error.field}:</strong> {error.message}
                                </Text>
                            ))}
                        </Stack>
                    </Alert>
                )}

                {mode !== 'view' && form.validationResult.warnings && form.validationResult.warnings.length > 0 && (
                    <Alert color="yellow" title="Validation Warnings" icon={<IconInfoCircle />}>
                        <Stack gap="xs">
                            {form.validationResult.warnings.map((warning, index) => (
                                <Text key={index} size="sm">
                                    <strong>{warning.field}:</strong> {warning.message}
                                    {warning.suggestion && <Text c="dimmed"> • {warning.suggestion}</Text>}
                                </Text>
                            ))}
                        </Stack>
                    </Alert>
                )}

                {/* Basic Job Information */}
                <Card withBorder>
                    <Title order={4} mb="md">Job Details</Title>
                    <Stack gap="md">
                        <Group grow>
                            <Select
                                label="Job Type"
                                data={jobTypeOptions}
                                required
                                disabled={mode === 'view'}
                                {...(mode !== 'view' ? form.getInputProps('jobType') : { value: job?.jobType })}
                                error={mode !== 'view' ? form.getFieldError('jobType') : undefined}
                            />

                            <Select
                                label="Priority"
                                data={priorityOptions}
                                disabled={mode === 'view'}
                                {...(mode !== 'view' ? form.getInputProps('priority') : { value: job?.priority })}
                                error={mode !== 'view' ? form.getFieldError('priority') : undefined}
                            />
                        </Group>

                        <TextInput
                            label="Job Name"
                            placeholder="Data processing job"
                            required
                            disabled={mode === 'view'}
                            {...(mode !== 'view' ? form.getInputProps('name') : { value: job?.name || '' })}
                            error={mode !== 'view' ? form.getFieldError('name') : undefined}
                        />

                        <Textarea
                            label="Description"
                            placeholder="Detailed description of what this job does..."
                            minRows={3}
                            disabled={mode === 'view'}
                            {...(mode !== 'view' ? form.getInputProps('description') : { value: job?.description || '' })}
                            error={mode !== 'view' ? form.getFieldError('description') : undefined}
                        />
                    </Stack>
                </Card>

                {/* Execution Settings */}
                <Card withBorder>
                    <Group justify="space-between" mb="md">
                        <Title order={4}>Execution Settings</Title>
                        {mode !== 'view' && (
                            <Button
                                variant="light"
                                size="xs"
                                onClick={() => setShowAdvanced(!showAdvanced)}
                            >
                                {showAdvanced ? 'Hide' : 'Show'} Advanced
                            </Button>
                        )}
                    </Group>

                    <Stack gap="md">
                        <Group grow>
                            <NumberInput
                                label="Max Retries"
                                min={0}
                                max={10}
                                disabled={mode === 'view'}
                                {...(mode !== 'view' ? form.getInputProps('maxRetries') : { value: job?.maxRetries || 0 })}
                                error={mode !== 'view' ? form.getFieldError('maxRetries') : undefined}
                            />

                            <NumberInput
                                label="Retry Backoff (ms)"
                                min={1000}
                                step={1000}
                                disabled={mode === 'view'}
                                {...(mode !== 'view' ? form.getInputProps('retryBackoff') : { value: job?.retryBackoff || 0 })}
                                error={mode !== 'view' ? form.getFieldError('retryBackoff') : undefined}
                            />
                        </Group>

                        <Group grow>
                            <DateTimePicker
                                label="Scheduled For"
                                placeholder="Leave empty for immediate execution"
                                disabled={mode === 'view'}
                                value={mode !== 'view' ?
                                    (form.values.scheduledFor ? new Date(form.values.scheduledFor) : null) :
                                    null
                                }
                                onChange={mode !== 'view' ?
                                    (date) => form.setFieldValue('scheduledFor', date ? String(date) : null) :
                                    undefined
                                }
                                error={mode !== 'view' ? form.getFieldError('scheduledFor') : undefined}
                            />

                            <Select
                                label="Timeout"
                                placeholder="Select timeout"
                                data={timeoutPresets.map(preset => ({
                                    value: preset.value.toString(),
                                    label: preset.label
                                }))}
                                disabled={mode === 'view'}
                                value={mode !== 'view' ?
                                    form.values.timeout?.toString() :
                                    undefined
                                }
                                onChange={mode !== 'view' ?
                                    (value: string | null) => form.setFieldValue('timeout', value ? parseInt(value) : undefined) :
                                    undefined
                                }
                                error={mode !== 'view' ? form.getFieldError('timeout') : undefined}
                            />
                        </Group>

                        {(showAdvanced || mode === 'view') && (
                            <JsonInput
                                label="Job Parameters (JSON)"
                                placeholder='{ "inputFile": "data.csv", "outputFormat": "json" }'
                                validationError="Invalid JSON"
                                formatOnBlur
                                autosize
                                minRows={4}
                                maxRows={10}
                                disabled={mode === 'view'}
                                value={mode !== 'view' ?
                                    (typeof form.values.parameters === 'string' ?
                                        form.values.parameters :
                                        JSON.stringify(form.values.parameters || {}, null, 2)) :
                                    JSON.stringify(job?.parameters || {}, null, 2)
                                }
                                onChange={mode !== 'view' ?
                                    (value: string) => {
                                        try {
                                            const parsed = JSON.parse(value || '{}');
                                            form.setFieldValue('parameters', parsed);
                                        } catch {
                                            form.setFieldValue('parameters', value);
                                        }
                                    } :
                                    undefined
                                }
                                error={mode !== 'view' ? form.getFieldError('parameters') : undefined}
                            />
                        )}
                    </Stack>
                </Card>

                {/* Job Logs (View Mode) */}
                {mode === 'view' && (
                    <Card withBorder>
                        <Group justify="space-between" mb="md">
                            <Title order={4}>Job Logs</Title>
                            <Group>
                                <Button
                                    variant="light"
                                    size="xs"
                                    onClick={() => setShowLogs(!showLogs)}
                                >
                                    {showLogs ? 'Hide' : 'Show'} Logs
                                </Button>
                                <ActionIcon onClick={refetchLogs} variant="default" size="sm">
                                    <IconRefresh size={16} />
                                </ActionIcon>
                            </Group>
                        </Group>

                        {showLogs && (
                            <Stack gap="sm" mah={400} style={{ overflow: 'auto' }}>
                                {logsLoading ? (
                                    <Progress value={30} animated />
                                ) : logs.length > 0 ? (
                                    <Timeline>
                                        {logs.map((log, index) => (
                                            <Timeline.Item key={index}>
                                                <Group>
                                                    <Badge
                                                        size="xs"
                                                        color={
                                                            log.level === 'error' ? 'red' :
                                                                log.level === 'warn' ? 'orange' :
                                                                    log.level === 'info' ? 'blue' : 'gray'
                                                        }
                                                    >
                                                        {log.level.toUpperCase()}
                                                    </Badge>
                                                    <Text size="xs" c="dimmed">
                                                        {new Date(log.timestamp).toLocaleTimeString()}
                                                    </Text>
                                                </Group>
                                                <Text size="sm" mt={4}>
                                                    {log.message}
                                                </Text>
                                                {(log as any).context && (
                                                    <Code block mt={4}>
                                                        {JSON.stringify((log as any).context, null, 2)}
                                                    </Code>
                                                )}
                                            </Timeline.Item>
                                        ))}
                                    </Timeline>
                                ) : (
                                    <Text c="dimmed" ta="center" py="xl">
                                        No logs available for this job
                                    </Text>
                                )}
                            </Stack>
                        )}
                    </Card>
                )}

                {/* Form Actions */}
                {mode !== 'view' && (
                    <Paper p="md" withBorder>
                        <Group justify="space-between">
                            <div>
                                <Text size="sm" c="dimmed">
                                    Job will be validated before submission
                                </Text>
                                {form.isValidating && (
                                    <Text size="sm" c="blue">
                                        Validating...
                                    </Text>
                                )}
                            </div>

                            <Group>
                                <Button variant="light" onClick={onCancel}>
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    loading={submitting || form.isValidating}
                                    disabled={!form.validationResult.valid}
                                >
                                    {mode === 'create' ? 'Create Job' : 'Update Job'}
                                </Button>
                            </Group>
                        </Group>
                    </Paper>
                )}

                {/* Submission Result */}
                {submissionResult && (
                    <Alert
                        color={submissionResult.success ? 'green' : 'red'}
                        title={submissionResult.success ? 'Success' : 'Error'}
                    >
                        {submissionResult.success ?
                            'Job saved successfully!' :
                            submissionResult.error
                        }
                    </Alert>
                )}
            </Stack>
        </form>
    );
};

export default ValidatedJobStatusForm;